import { useEffect, useState } from 'react';
import { getContactsList, getJoinedPairs } from '../../services/call.service';
import { getLocationFeatureStatus } from '../../../../../util/APIUtils';
import { ManagementService } from '../../../../../components/management/management.service';
import CallIcon from '@mui/icons-material/Call';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { PhotoViewer } from '../picture-viewer';
import { generateStatusChangeOptions } from '../../../../../components/voice-call/utils/utils';
import React from 'react';
import { mapRelationshipType, mapVideoCallRelationshipType } from '../../../../../util/relationshipTypeMapper';

export const useAllContactsTabHook = ({ locationId, addContactModal, isEditContactModalOpen, updateContactStatus, handleEditContactModalOpen }) => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [videoCallStatus, setVideoCallStatus] = useState(false);
  const [sort, setSort] = useState({
    column: "",
    direction: ""
  });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [videoCallContactProps, setVideoCallContactProps] = useState({
    isOpen: false,
    userId: null,
  });
  const [, setSelectedRows] = useState([]);
  const [columns, setColumns] = useState([
    {
      name: 'LearnerCell',
      preset: 'LearnerCell',
       filter: {
        type: 'LearnerCell'
      },
      displayName: 'Resident',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'preDefined',
      sortByProp: 'learnerUsername', // Can be 'username', 'firstName', 'lastName' or 'docId'
    },
    {
      name: 'contactName',
      displayName: 'Contact Name',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
      sortByProp: 'contactFirstName,contactLastName'
    },
    {
      name: 'contactPhoneNumber',
      displayName: 'Phone Number',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
    },
    {
      name: 'relationshipType',
      displayName: 'Relationship',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
    },
    {
      name: 'relationshipStatus',
      displayName: 'Contact Status',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
    },
    {
      name: 'productType',
      displayName: 'Contact Type',
      sortOrder: 'none',
      isFilterable: true,
      isVisible: true,
      type: 'override',
    }
  ]);


  useEffect(() => { //WORKAROUND IN ORDER TO GET REFRESHED ITEMS
      refreshContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshContacts intentionally not in deps
  }, [isEditContactModalOpen, addContactModal]);



  // Function to map contact data to required row format
  const mapContactsToRows = (contacts) => {
    if (!contacts || !Array.isArray(contacts)) return [];
    return contacts.map((contact, index) => {
      // Map relationship type based on product type
      const mappedRelationshipType = contact.productType === ManagementService.MANAGEMENT_FEATURE_ENUM.VideoCalls.toUpperCase()
        ? mapVideoCallRelationshipType(contact.relationshipType)
        : mapRelationshipType(contact.relationshipType);

      return {
        isSelected: false, // Default selection state
        rowData: {
          id: contact.id,
          contactName: `${contact.contactFirstName || ''} ${contact.contactLastName || ''}`.trim(),
          contactPhoneNumber: contact.contactPhoneNumber || '',
          relationshipType: mappedRelationshipType,
          relationshipStatus: contact.relationshipStatus || '',
          contactId: contact.contactId,
          personKey: contact.personKey, // Added personKey to row data
          learnerUsername: contact.learnerUsername || '',
          productType: () => (
            <>
              {contact.productType === ManagementService.MANAGEMENT_FEATURE_ENUM.VoiceCalls.toUpperCase() && (
                <CallIcon />
              )}
              {contact.productType === ManagementService.MANAGEMENT_FEATURE_ENUM.VideoCalls.toUpperCase() && (
                <VideoCallIcon />
              )}
            </>
          ),
          contactPhoto: () => (
            <>
              {contact.productType === ManagementService.MANAGEMENT_FEATURE_ENUM.VideoCalls.toUpperCase() && contact.contactIdDocumentObjectKey && (
                <PhotoViewer details={contact}  />
              )}
              {(contact.productType === ManagementService.MANAGEMENT_FEATURE_ENUM.VoiceCalls.toUpperCase() || contact.contactIdDocumentObjectKey === '') && (
                'N/A'
              )}
            </>
          ),
        },
        preDefineComponentProps: {
          LearnerCell: {
            username: contact.learnerUsername,
            firstName: contact.learnerFirstName,
            lastName: contact.learnerLastName,
            docId: contact.docId,
          },
          actions: generateStatusChangeOptions({
            id: contact.id,
            firstName: contact.contactFirstName,
            lastName: contact.contactLastName,
            phoneNumber: contact.contactPhoneNumber,
            learnerPersonKey: contact.personKey,
            relationshipType: contact.relationshipType,
            preferredLanguage: contact.contactPreferredLanguage,
            learnerUsername: contact.learnerUsername,
            status: contact.relationshipStatus,
            productType: contact.productType,
            contactId: contact.contactId
          }, updateContactStatus, editContact, refreshContacts)
          .filter(option => {
            if (contact.productType === ManagementService.MANAGEMENT_FEATURE_ENUM.VideoCalls.toUpperCase()) {
              if (option.title === 'Edit') {
                return true;
              }
              return false;
            }
            return true;
          })
        },
      };
    });
  };

  const getVideoCallStatus = async () => {
    try {
      const featureStatusResponse = await getLocationFeatureStatus(locationId, ManagementService.MANAGEMENT_FEATURE_ENUM.VideoCalls);
      setVideoCallStatus(featureStatusResponse.status);
      if (featureStatusResponse.status) {
        const contactPhotoColumn = {
          name: 'contactPhoto',
          displayName: 'Contact Photo',
          sortOrder: 'none',
          isFilterable: false,
          isSortable: false,
          isVisible: true,
          type: 'override',
        };
        setColumns((columns) => {
          // Check if contactPhoto column already exists to prevent duplicates
          const hasContactPhotoColumn = columns.some(col => col.name === 'contactPhoto');
          if (!hasContactPhotoColumn) {
            return [...columns, contactPhotoColumn];
          }
          return columns;
        });
      }
    } catch (e) {
      // Feature status error; videoCallStatus remains false
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    // Clear existing data immediately when starting a new fetch to prevent showing old data
    setData([]);
    setTotalRecords(0);
    
    try {
      const response = await getContactsList({
        locationId,
        size: pageSize,
        page: page - 1,
        ...sort,
        filter
      }); // Get all contacts with size parameter
      const contactsData = response?.data?.content || [];
      // Only set new data, completely replacing any previous data
      setData(mapContactsToRows(contactsData.data.content));
      setTotalRecords(contactsData.data.totalElements);
    } catch (err) {
      setError(err);
      // On error, ensure data remains empty to avoid showing stale data
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
      setLastRefreshed(new Date());
    }
  };

  useEffect(() => {
    getVideoCallStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    fetchContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchContacts uses latest state internally
  }, [locationId, page, pageSize, sort, filter]);

  // Function to format the timestamp
  const formatLastRefreshedTime = (timestamp) => {
    if (!timestamp) return '';

    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', options)}`;
  };

  // Refresh function to be called externally
  const refreshContacts = async () => {
    await fetchContacts();
  };

  const editContact = (rowData) => {
    switch (rowData.productType) {
      case ManagementService.MANAGEMENT_FEATURE_ENUM.VoiceCalls.toUpperCase():
        handleEditContactModalOpen(rowData);
        break;
      case ManagementService.MANAGEMENT_FEATURE_ENUM.VideoCalls.toUpperCase():
        toggleAddVideoContactModal(rowData.contactId);
        break;
        default:
        break;
    }
  };

  const toggleAddVideoContactModal = (userId) => {
    setVideoCallContactProps((prev) => {
      const updatedOpenValue = !prev.isOpen;
      if (updatedOpenValue && userId) {
        return {
          isOpen: updatedOpenValue,
          userId: userId,
        };
      }
      return {
        isOpen: updatedOpenValue,
      };
    });
    fetchContacts();
  };

  const onPageChange = async (page) => {
    setPage(page);
  };

  const onPageSizeChange = (pageSize) => {
    setPageSize(pageSize);
    setPage(1); // Reset to first page when page size changes
  }

  const getSortColumnName = (column) => {
    const searchedColumn = columns.find(col => col.name === column);
    if (searchedColumn.sortByProp) {
      return searchedColumn.sortByProp;
    }
    return searchedColumn.name;
  };

  const onSortChange = (column, direction) => {
    setSort({
      sort: getSortColumnName(column),
      direction
    });
    
  };

  const onFilterChange = (filters) => {
    const filter = {};
    const filterKeyMapper = {
      "username": "learnerUsername",
      "firstName": "learnerFirstName",
      "lastName": "learnerLastName",
      "docId": "docId"
    };
    filters.forEach(
      (item) => {
        let filterKey = item.preDefinedPropName ? filterKeyMapper[item.preDefinedPropName] || item.columnName : item.columnName;
        if (filter.hasOwnProperty(filterKey)) {
          filter[filterKey] += `,${item.value}`
        } else {
          filter[filterKey] = item.value;
        }
      }
    );

    setFilter(getJoinedPairs(filter));
    setPage(1); // Reset to first page when filter changes
    setSelectedRows([]); // Clear selected rows when filter changes to prevent old selections
  }
const handleSelectedChange = (selectedData) => {

  const selectedKeySet = new Set(
    selectedData.map(
      item => `${item.rowData.contactId}_${item.rowData.personKey}`
    )
  );

  setSelectedRows(Array.from(selectedKeySet));
};


  return {
    videoCallStatus,
    columns,
    pageSize,
    totalRecords,
    data,
    setData,
    loading,
    error,
    mapContactsToRows,
    refreshContacts,
    lastRefreshed,
    formatLastRefreshedTime,
    videoCallContactProps,
    toggleAddVideoContactModal,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onFilterChange,
    handleSelectedChange,
  };
};
